import { ThumbDownIcon, ThumbUpIcon } from 'components/common/Icons';
import { uniqueId } from 'lodash';

import {
  Avatar,
  Button,
  Flex,
  IconButton,
  reactIcons as Icons,
  Tooltip,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

const styles = {
  chatBubble: {
    borderRadius: '$radius-small',
    backgroundColor: '$palette-grey-700',
    color: '$text-contrast',
    padding: '$small',
  },
  chatButton: {
    fontWeight: '$normal',
    height: 'fit-content',
    textAlign: 'left',
    whiteSpace: 'normal',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: '$palette-grey-800',
    },
  },
  infoBubble: {
    color: '$palette-indigo-100',
  },
  saveBox: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 38,
  },
  ratingBox: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 30,
  },
};

const formatTime = (date: Date) =>
  date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

export interface IChatBubble {
  id?: string;
  createdAt?: Date;
  filterName?: string;
  handleFilters?: () => void;
  handleRate?: (isApproved: boolean) => Promise<void>;
  handleRestore?: () => void;
  handleSave?: () => void;
  icon?: JSX.Element;
  isBot?: boolean;
  isHeader?: boolean;
  isInfo?: boolean;
  isRatedBad?: boolean;
  isRatedGood?: boolean;
  isSaved?: boolean;
  ariaDetails?: string;
  // TODO handle to allow icon
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  message: any;
}

function ChatBubble({
  createdAt,
  filterName,
  handleFilters,
  handleRate,
  handleSave,
  icon,
  isBot,
  isHeader,
  isInfo,
  isRatedBad,
  isRatedGood,
  isSaved,
  message,
  ariaDetails,
}: IChatBubble) {
  const { t } = useTranslation();

  if (isHeader) {
    return (
      <Flex key={uniqueId()} orientation="vertical" align="center">
        {icon}
        <Typography variant="subtitle" css={styles.infoBubble}>
          {message}
        </Typography>
      </Flex>
    );
  }

  return (
    <Flex key={uniqueId()} gap="small">
      {icon && (
        <Avatar
          fallback={icon}
          css={{ bgColor: isBot ? '$palette-grey-900' : '' }}
        />
      )}
      <Flex
        css={{ width: isInfo ? '100%' : '' }}
        gap="x-small"
        orientation="vertical"
      >
        {handleFilters ? (
          <Button
            aria-label={t('core.chat.setFilter', { name: filterName })}
            onPress={handleFilters}
            css={{ ...styles.chatBubble, ...styles.chatButton }}
            endIcon={<Icons.ArrowRight />}
            children={message}
            {...(ariaDetails && { 'aria-details': ariaDetails })}
          />
        ) : (
          <Typography
            variant="subtext"
            css={isInfo && isBot ? styles.infoBubble : styles.chatBubble}
            children={message}
          />
        )}
        {createdAt && (
          <Flex justify="between">
            <Flex gap="x-small" css={{ color: '$text-contrast' }}>
              <Typography variant="captionBold">
                {t(`core.chat.${isBot ? 'movementGpt' : 'you'}`)}
              </Typography>
              <Typography variant="caption">&#x2022;</Typography>
              <Typography variant="caption">{formatTime(createdAt)}</Typography>
            </Flex>
            {handleSave && handleRate && (
              <Flex justify="end" gap="x-small">
                {!isSaved && (
                  <Tooltip
                    title={t('core.chat.saveView', { name: filterName })}
                  >
                    <IconButton
                      aria-label={t('core.chat.saveView', { name: filterName })}
                      onPress={handleSave}
                      size="small"
                      variant="tertiary"
                      children={<Icons.Add css={{ color: '$text-contrast' }} />}
                    />
                  </Tooltip>
                )}
                {isSaved && (
                  <Tooltip title={t('core.chat.viewCreated')}>
                    <Flex css={styles.saveBox}>
                      <Icons.Check css={{ color: '$palette-green-200' }} />
                    </Flex>
                  </Tooltip>
                )}
                {(isRatedGood || isRatedBad) && (
                  <>
                    <Tooltip title={t('core.chat.feedbackGood')}>
                      <Flex
                        css={{
                          ...styles.ratingBox,
                          visibility: isRatedBad ? 'hidden' : '',
                        }}
                      >
                        <Icons.Check css={{ color: '$palette-green-200' }} />
                      </Flex>
                    </Tooltip>
                    <Tooltip title={t('core.chat.feedbackBad')}>
                      <Flex
                        css={{
                          ...styles.ratingBox,
                          visibility: isRatedGood ? 'hidden' : '',
                        }}
                      >
                        <Icons.Check css={{ color: '$palette-red-200' }} />
                      </Flex>
                    </Tooltip>
                  </>
                )}
                {!isRatedGood && !isRatedBad && (
                  <>
                    <Tooltip title={t('core.chat.responseGood')}>
                      <IconButton
                        aria-label={t('core.chat.responseGood')}
                        size="small"
                        onPress={() => handleRate(true)}
                        variant="tertiary"
                        children={<ThumbUpIcon />}
                      />
                    </Tooltip>
                    <Tooltip title={t('core.chat.responseBad')}>
                      <IconButton
                        aria-label={t('core.chat.responseBad')}
                        size="small"
                        onPress={() => handleRate(false)}
                        variant="tertiary"
                        children={<ThumbDownIcon />}
                      />
                    </Tooltip>
                  </>
                )}
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

export default ChatBubble;
